import axios, { type AxiosInstance } from "axios"
import { getExhibitionHeader } from "../utils/exhibitionContext";

export interface UserProfile {
  user_id: number
  email: string
  is_superuser: boolean
  is_staff: boolean
  is_active: boolean
  person: PersonProfile | null
}

export interface PersonProfile {
  id: number
  first_name: string
  middle_name: string | null
  last_name: string
  full_name: string
  email: string
  phone: string
  id_doc_type: number | null
  id_doc_type_name: string | null
  id_doc_number: string | null
  id_doc_attachment: string | null
  photo_path: string | null
  status: boolean
  created_at: string | null
  updated_at: string | null
}

export interface UpdateProfileData {
  first_name?: string
  middle_name?: string
  last_name?: string
  phone?: string
  id_doc_type?: number
  id_doc_number?: string
  photo_base64?: string
  id_doc_base64?: string
}

export interface IdDocumentType {
  id: number
  name: string
  length: number | null
  requires_country: boolean
}

const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://127.0.0.1:8000",
  headers: {
    "Content-Type": "application/json",
  },
})

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken")
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`
    
    Object.assign(config.headers, getExhibitionHeader());}
    return config
  },
  (error) => Promise.reject(error),
)

export const ProfileService = {
  // Get current user's profile
  getProfile: async (): Promise<UserProfile> => {
    const response = await api.get("/api/accounts/profile/")
    return response.data
  },

  // Update user profile
  updateProfile: async (data: UpdateProfileData): Promise<{ message: string; profile: UserProfile }> => {
    const response = await api.put("/api/accounts/profile/update/", data)
    return response.data
  },

  // Upload profile photo
  uploadPhoto: async (file: File): Promise<{ message: string; photo_url: string }> => {
    const formData = new FormData()
    formData.append("photo", file)

    const response = await api.put("/api/accounts/profile/update/", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    return response.data
  },

  // Upload ID document
  uploadIdDocument: async (file: File): Promise<{ message: string; doc_url: string }> => {
    const formData = new FormData()
    formData.append("id_doc_attachment", file)

    const response = await api.put("/api/accounts/profile/update/", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    return response.data
  },

  // Get ID document types
  getIdDocumentTypes: async (): Promise<IdDocumentType[]> => {
    const response = await api.get("/api/accounts/get-id-document-types/")
    return response.data
  },

  // Convert file to base64
  fileToBase64: (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = (error) => reject(error)
    })
  },
}
